// PCIDSSAnalyser.jsx
import React, { useState, useEffect, useCallback } from 'react';
import NavigationPanel from './NavigationPanel';
import BuyMeACoffee from './coffee';

// 1) Import icons from lucide-react
import { Copy, CheckCircle2 } from 'lucide-react';

const PCIDSSAnalyser = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [policyText, setPolicyText] = useState('');

  // Existing toggles
  const [checkWildcards, setCheckWildcards] = useState(true);
  const [checkResourceScope, setCheckResourceScope] = useState(true);
  const [checkMFA, setCheckMFA] = useState(false);
  const [checkKeyRotation, setCheckKeyRotation] = useState(false);
  const [checkDestructive, setCheckDestructive] = useState(false);

  // Additional toggles
  const [checkCloudTrail, setCheckCloudTrail] = useState(false);
  const [checkSecureTransport, setCheckSecureTransport] = useState(false);
  const [checkEncryptionAtRest, setCheckEncryptionAtRest] = useState(false);

  // **New** toggles for deeper checks
  const [checkRootUserUsage, setCheckRootUserUsage] = useState(false);
  const [checkAdminPolicy, setCheckAdminPolicy] = useState(false);
  const [checkPublicPrincipals, setCheckPublicPrincipals] = useState(false);
  const [checkPolicyVersion, setCheckPolicyVersion] = useState(false);
  const [checkExplicitDenies, setCheckExplicitDenies] = useState(false);
  const [checkPasswordPolicy, setCheckPasswordPolicy] = useState(false);

  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // 2) Keep a copied state for "Copied!" feedback
  const [copied, setCopied] = useState(false);

  // Patterns to highlight in the policy text
  // Each category has `enabled` + `patterns`; if any pattern matches a line => highlight line
  const highlightCategories = [
    {
      enabled: checkWildcards,
      patterns: [
        /[A-Za-z0-9_-]+:\*/,
        /"[Aa]ction"\s*:\s*"\*"/,
        /[“”][Aa]ction[“”]\s*:\s*[“”]\*[“”]/,
        /'[Aa]ction'\s*:\s*'\*'/
      ]
    },
    {
      enabled: checkResourceScope,
      patterns: [
        /"[Rr]esource"\s*:\s*"\*"/,
        /[“”][Rr]esource[“”]\s*:\s*[“”]\*[“”]/,
        /'[Rr]esource'\s*:\s*'\*'/,
        /[Rr]esource\s*:\s*\*/
      ]
    },
    {
      enabled: checkMFA,
      patterns: [
        /MultiFactorAuth/,
        /aws:MultiFactorAuthAge/
      ]
    },
    {
      enabled: checkDestructive,
      patterns: [
        /iam:PassRole/,
        /logs:DeleteLogGroup/,
        /ec2:TerminateInstances/,
        /kms:DisableKey/,
        /s3:DeleteBucket/
      ]
    },
    {
      enabled: checkCloudTrail,
      patterns: [
        /cloudtrail:StopLogging/i,
        /cloudtrail:DeleteTrail/i,
        /cloudtrail:UpdateTrail/i
      ]
    },
    {
      enabled: checkSecureTransport,
      patterns: [
        /"aws:SecureTransport"/i,
        /aws:SecureTransport/i
      ]
    },
    {
      enabled: checkEncryptionAtRest,
      patterns: [
        /kms:Encrypt/,
        /kms:Decrypt/,
        /kms:ReEncrypt/,
        /kms:GenerateDataKey/,
        /x-amz-server-side-encryption/,
        /aws:kms/i
      ]
    },
    // **New** highlight checks
    {
      enabled: checkRootUserUsage,
      patterns: [
        // Checking if policy references "Root" or root user arn
        // e.g. "arn:aws:iam::123456789012:root"
        /:root/i,
      ]
    },
    {
      enabled: checkAdminPolicy,
      patterns: [
        // If the policy references the "AdministratorAccess" managed policy
        /AdministratorAccess/,
      ]
    },
    {
      enabled: checkPublicPrincipals,
      patterns: [
        // Searching for "Principal": "*" in resource-based policy
        /"Principal"\s*:\s*"[*]"/i,
        /'Principal'\s*:\s*'[*]'/i
      ]
    },
    {
      enabled: checkPolicyVersion,
      patterns: [
        // Searching if there's an explicit "Version": "2012-10-17"
        /"Version"\s*:\s*"(.+?)"/i
      ]
    },
    {
      enabled: checkExplicitDenies,
      patterns: [
        // Searching for "Effect": "Deny"
        /"Effect"\s*:\s*"Deny"/i
      ]
    },
    {
      enabled: checkPasswordPolicy,
      patterns: [
        // e.g., references to password policy
        /"iam:SetDefaultPolicyVersion"/i,
        /"iam:UpdateAccountPasswordPolicy"/i
      ]
    }
  ];

  // For each line of policy text, if any pattern matches => highlight entire line
  const highlightPolicy = useCallback(() => {
    if (!policyText.trim()) return '';

    const lines = policyText.split('\n');
    const highlightedLines = lines.map((line) => {
      const safeLine = escapeHtml(line);
      let hasIssue = false;

      for (const category of highlightCategories) {
        if (!category.enabled) continue;
        for (const pattern of category.patterns) {
          if (pattern.test(line)) {
            hasIssue = true;
            break;
          }
        }
        if (hasIssue) break;
      }

      return hasIssue
        ? `<div class="bg-amber-500 text-gray-900 px-1 rounded mb-1">${safeLine}</div>`
        : `<div class="mb-1">${safeLine}</div>`;
    });

    return highlightedLines.join('');
  }, [
    policyText,
    checkWildcards,
    checkResourceScope,
    checkMFA,
    checkKeyRotation,
    checkDestructive,
    checkCloudTrail,
    checkSecureTransport,
    checkEncryptionAtRest,
    checkRootUserUsage,
    checkAdminPolicy,
    checkPublicPrincipals,
    checkPolicyVersion,
    checkExplicitDenies,
    checkPasswordPolicy,
    highlightCategories
  ]);

  // ------------------------------------------
  // Analyze policy: produce textual warnings/findings
  // ------------------------------------------
  const analyzePolicy = useCallback(() => {
    if (!policyText.trim()) {
      setAnalysisResult('No policy text provided.');
      return;
    }
    setIsAnalyzing(true);

    const lines = policyText.split('\n').map((l) => l.trim());
    const findings = [];

    // We'll do some JSON-level checks too (try parse)
    let policyJSON = null;
    try {
      policyJSON = JSON.parse(policyText);
    } catch (err) {
      // If it's not valid JSON, highlight that first
      findings.push('❌ The policy text is not valid JSON. Some checks may be inaccurate.');
    }

    // 1) Wildcards
    if (checkWildcards) {
      const wildcardLines = lines.filter((ln) =>
        ln.match(/[A-Za-z0-9_-]+:\*/) ||
        ln.match(/"[Aa]ction"\s*:\s*"\*"/) ||
        ln.match(/[“”][Aa]ction[“”]\s*:\s*[“”]\*[“”]/) ||
        ln.match(/'[Aa]ction'\s*:\s*'\*'/)
      );
      if (wildcardLines.length > 0) {
        findings.push(`⚠️ Found wildcard usage in ${wildcardLines.length} line(s). This can violate PCI DSS principle of least privilege.`);
      }
    }

    // 2) Resource scope
    if (checkResourceScope) {
      const resourceAll = lines.filter((ln) =>
        ln.match(/"[Rr]esource"\s*:\s*"\*"/) ||
        ln.match(/[“”][Rr]esource[“”]\s*:\s*[“”]\*[“”]/) ||
        ln.match(/'[Rr]esource'\s*:\s*'\*'/) ||
        ln.match(/[Rr]esource\s*:\s*\*/)
      );
      if (resourceAll.length > 0) {
        findings.push(`⚠️ Found Resource = "*" in ${resourceAll.length} line(s). PCI DSS recommends scoping resources explicitly.`);
      }
    }

    // 3) MFA
    if (checkMFA) {
      if (
        !policyText.includes('MultiFactorAuth') &&
        !policyText.includes('aws:MultiFactorAuthAge')
      ) {
        findings.push(
          `⚠️ No explicit MFA-related tokens found. PCI-DSS strongly encourages MFA for all privileged accounts.`
        );
      }
    }

    // 4) Key Rotation
    if (checkKeyRotation) {
      if (
        !policyText.includes('RotateAccessKey') &&
        !policyText.includes('RotateKeys')
      ) {
        findings.push(
          `⚠️ No mention of key rotation. PCI-DSS requires frequent credential rotation.`
        );
      }
    }

    // 5) Destructive
    if (checkDestructive) {
      const destructiveList = [
        'iam:PassRole',
        'logs:DeleteLogGroup',
        'ec2:TerminateInstances',
        'kms:DisableKey',
        's3:DeleteBucket'
      ];
      let foundCount = 0;
      lines.forEach((ln) => {
        destructiveList.forEach((pat) => {
          if (ln.includes(pat)) {
            foundCount++;
          }
        });
      });
      if (foundCount > 0) {
        findings.push(
          `⚠️ Found ${foundCount} mention(s) of destructive/risky actions (e.g. PassRole, DeleteBucket). PCI-DSS requires tight controls around these.`
        );
      }
    }

    // 6) CloudTrail Integrity
    if (checkCloudTrail) {
      const suspiciousCT = lines.filter((ln) =>
        ln.match(/cloudtrail:StopLogging/i) ||
        ln.match(/cloudtrail:DeleteTrail/i) ||
        ln.match(/cloudtrail:UpdateTrail/i)
      );
      if (suspiciousCT.length > 0) {
        findings.push(
          `⚠️ Found potential CloudTrail manipulation (StopLogging/DeleteTrail/UpdateTrail) in ${suspiciousCT.length} line(s). PCI DSS requires tamper-evident logging.`
        );
      }
    }

    // 7) Secure Transport
    if (checkSecureTransport) {
      if (!lines.some((ln) => ln.match(/aws:SecureTransport/i))) {
        findings.push(
          `⚠️ No mention of 'aws:SecureTransport' condition. Consider enforcing TLS to meet PCI-DSS encryption-in-transit requirements.`
        );
      }
    }

    // 8) Encryption at Rest
    if (checkEncryptionAtRest) {
      const encryptionKeywords = [
        'kms:Encrypt',
        'kms:Decrypt',
        'kms:ReEncrypt',
        'kms:GenerateDataKey',
        'x-amz-server-side-encryption',
        'aws:kms'
      ];
      const foundEncryption = lines.some((ln) =>
        encryptionKeywords.some((kw) => ln.includes(kw))
      );
      if (!foundEncryption) {
        findings.push(
          `⚠️ No mention of KMS or SSE usage. Verify that data is encrypted at rest per PCI-DSS.`
        );
      }
    }

    // 9) Root user usage
    if (checkRootUserUsage) {
      // Looking for `:root` or references to root in the lines
      const rootRefs = lines.filter((ln) => ln.match(/:root/i));
      if (rootRefs.length > 0) {
        findings.push(
          `⚠️ Found references to the root user in ${rootRefs.length} line(s). PCI-DSS recommends avoiding root for daily operations.`
        );
      }
    }

    // 10) AdministratorAccess policy references
    if (checkAdminPolicy) {
      const adminPolicyRefs = lines.filter((ln) => ln.match(/AdministratorAccess/i));
      if (adminPolicyRefs.length > 0) {
        findings.push(
          `⚠️ Found references to the AWS-managed AdministratorAccess policy. This grants broad privileges that may exceed PCI-DSS minimal requirements.`
        );
      }
    }

    // 11) Public principals in resource-based policies
    if (checkPublicPrincipals) {
      const publicRefs = lines.filter((ln) =>
        ln.match(/"Principal"\s*:\s*"[*]"/i) ||
        ln.match(/'Principal'\s*:\s*'[*]'/i)
      );
      if (publicRefs.length > 0) {
        findings.push(
          `⚠️ Found public ("Principal": "*") references in ${publicRefs.length} line(s). This can open resources to anonymous access.`
        );
      }
    }

    // 12) Policy version
    if (checkPolicyVersion) {
      // If we have valid JSON, look for "Version": "2012-10-17" or otherwise
      if (policyJSON && policyJSON.Version !== '2012-10-17') {
        findings.push(
          `⚠️ Policy "Version" is "${policyJSON?.Version}" instead of "2012-10-17". Ensure your policy uses a recognized version.`
        );
      } else if (!policyJSON?.Version) {
        findings.push(
          `⚠️ No "Version" field found in policy. AWS IAM best practice is to specify "Version": "2012-10-17".`
        );
      }
    }

    // 13) Explicit Denies
    if (checkExplicitDenies && policyJSON && policyJSON.Statement) {
      // Let's see if there's any statement with "Effect": "Deny"
      const statements = Array.isArray(policyJSON.Statement)
        ? policyJSON.Statement
        : [policyJSON.Statement];
      const denyStmts = statements.filter((stmt) => stmt.Effect === 'Deny');
      if (denyStmts.length === 0) {
        findings.push(
          `⚠️ No "Effect": "Deny" statements found. PCI-DSS often encourages explicit denies to limit access.`
        );
      }
    }

    // 14) Password policy references
    if (checkPasswordPolicy) {
      const passPolicyLines = lines.filter((ln) =>
        ln.match(/iam:UpdateAccountPasswordPolicy/i)
      );
      if (passPolicyLines.length === 0) {
        findings.push(
          `⚠️ No references to updating the account password policy. PCI-DSS typically requires strong password settings.`
        );
      }
    }

    if (findings.length === 0) {
      findings.push('✅ Policy looks OK based on the checks you enabled!');
    }

    setAnalysisResult(findings.join('\n'));
    setIsAnalyzing(false);
  }, [
    policyText,
    checkWildcards,
    checkResourceScope,
    checkMFA,
    checkKeyRotation,
    checkDestructive,
    checkCloudTrail,
    checkSecureTransport,
    checkEncryptionAtRest,
    checkRootUserUsage,
    checkAdminPolicy,
    checkPublicPrincipals,
    checkPolicyVersion,
    checkExplicitDenies,
    checkPasswordPolicy
  ]);

  // Re-run analysis whenever text or toggles change
  useEffect(() => {
    analyzePolicy();
  }, [
    policyText,
    checkWildcards,
    checkResourceScope,
    checkMFA,
    checkKeyRotation,
    checkDestructive,
    checkCloudTrail,
    checkSecureTransport,
    checkEncryptionAtRest,
    checkRootUserUsage,
    checkAdminPolicy,
    checkPublicPrincipals,
    checkPolicyVersion,
    checkExplicitDenies,
    checkPasswordPolicy,
    analyzePolicy
  ]);

  // Escape HTML to safely display user input
  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const handleInput = (e) => {
    const plainText = e.currentTarget.innerText;
    setPolicyText(plainText);
  };

  const highlightedHtml = highlightPolicy();

  // 3) Copy function
  const copyToClipboard = useCallback(() => {
    if (!policyText) return;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = policyText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.top = '50%';
      textarea.style.left = '50%';
      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Failed to copy - please try selecting and copying manually');
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }, [policyText]);

  return (
    <>
      {/* 1) Embedded style for placeholder text */}
      <style>
        {`
          .pci-policy-input:empty::before {
            content: "Paste your IAM policy here (JSON format)...";
            color: #999;
            pointer-events: none;
          }
        `}
      </style>

      {/* Navigation */}
      <NavigationPanel darkMode={darkMode} setDarkMode={setDarkMode} />

      <div
        className={`
          min-h-screen p-4 md:p-8 transition-colors
          ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}
        `}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Left: contentEditable with updated scrollbar styling */}
            <div
              className={`
                md:col-span-2
                rounded-lg shadow p-6 border border-transparent space-y-4
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
              `}
            >
              {/* Copy button next to label */}
              <div className="flex items-center justify-between">
                <label className="block text-lg font-semibold">
                  Input Policy
                </label>

                <button
                  onClick={copyToClipboard}
                  className="
                    inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors
                    bg-[#FF9900] text-white hover:bg-[#FFB84D]
                  "
                >
                  {copied ? (
                    <>
                      <CheckCircle2 size={16} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy Policy
                    </>
                  )}
                </button>
              </div>

              {/* 2) Add the "pci-policy-input" class for placeholder rule */}
              <div
                contentEditable
                onInput={handleInput}
                spellCheck="false"
                className={`
                  pci-policy-input
                  w-full h-[650px] p-3 border border-amber-200 rounded-md font-mono text-sm
                  focus:outline-none resize-none transition-colors
                  scrollbar-thin
                  scrollbar-thumb-gray-500
                  scrollbar-track-gray-300
                  hover:scrollbar-thumb-gray-600
                  ${
                    darkMode
                      ? 'bg-gray-700 text-gray-100 border-gray-600 scrollbar-track-gray-700 scrollbar-thumb-gray-500 hover:scrollbar-thumb-gray-400'
                      : 'bg-gray-200 text-gray-800 border-gray-300'
                  }
                  whitespace-pre-wrap overflow-auto
                `}
                dangerouslySetInnerHTML={{ __html: highlightedHtml }}
              />

              {isAnalyzing && (
                <div className="text-sm text-amber-400">
                  Analyzing your policy...
                </div>
              )}
            </div>

            {/* Right: toggles + analysis result */}
            <div
              className={`
                rounded-lg shadow p-6 border border-transparent space-y-4
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
                w-full md:w-[480px]
              `}
            >
              <h2 className="text-lg font-semibold">PCI-DSS Checks</h2>

              {/* 
                Updated container: 2 toggles per row 
                (just changing layout, no logic change)
              */}
              <div className="grid grid-cols-2 gap-4">
                <ToggleRow
                  label="Wildcards"
                  enabled={checkWildcards}
                  onChange={() => setCheckWildcards(!checkWildcards)}
                />
                <ToggleRow
                  label="Resource Scope"
                  enabled={checkResourceScope}
                  onChange={() => setCheckResourceScope(!checkResourceScope)}
                />
                <ToggleRow
                  label="MFA Requirement"
                  enabled={checkMFA}
                  onChange={() => setCheckMFA(!checkMFA)}
                />
                <ToggleRow
                  label="Key Rotation"
                  enabled={checkKeyRotation}
                  onChange={() => setCheckKeyRotation(!checkKeyRotation)}
                />
                <ToggleRow
                  label="Risky Actions"
                  enabled={checkDestructive}
                  onChange={() => setCheckDestructive(!checkDestructive)}
                />
                <ToggleRow
                  label="CloudTrail Integrity"
                  enabled={checkCloudTrail}
                  onChange={() => setCheckCloudTrail(!checkCloudTrail)}
                />
                <ToggleRow
                  label="Secure Transport"
                  enabled={checkSecureTransport}
                  onChange={() => setCheckSecureTransport(!checkSecureTransport)}
                />
                <ToggleRow
                  label="Encryption at Rest"
                  enabled={checkEncryptionAtRest}
                  onChange={() => setCheckEncryptionAtRest(!checkEncryptionAtRest)}
                />
                <ToggleRow
                  label="Root User Usage"
                  enabled={checkRootUserUsage}
                  onChange={() => setCheckRootUserUsage(!checkRootUserUsage)}
                />
                <ToggleRow
                  label="Admin Policy Ref"
                  enabled={checkAdminPolicy}
                  onChange={() => setCheckAdminPolicy(!checkAdminPolicy)}
                />
                <ToggleRow
                  label="Public Principals"
                  enabled={checkPublicPrincipals}
                  onChange={() => setCheckPublicPrincipals(!checkPublicPrincipals)}
                />
                <ToggleRow
                  label="Policy Version"
                  enabled={checkPolicyVersion}
                  onChange={() => setCheckPolicyVersion(!checkPolicyVersion)}
                />
                <ToggleRow
                  label="Explicit Denies"
                  enabled={checkExplicitDenies}
                  onChange={() => setCheckExplicitDenies(!checkExplicitDenies)}
                />
                <ToggleRow
                  label="Password Policy"
                  enabled={checkPasswordPolicy}
                  onChange={() => setCheckPasswordPolicy(!checkPasswordPolicy)}
                />
              </div>

              {/* Analysis Results */}
              <div className="mt-6">
                <br />
                <h2 className="text-lg font-semibold mb-2">Analysis Results</h2>
                <pre
                  className={`
                    p-3 rounded-md border whitespace-pre-line text-sm
                    ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100'
                        : 'bg-gray-200 border-gray-300 text-gray-800'
                    }
                  `}
                >
                  {analysisResult || 'No analysis result yet.'}
                </pre>
              </div>

              {/* PCI DSS tips */}
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <p className="font-semibold">PCI DSS & IAM Best Practices:</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <strong>Principle of Least Privilege</strong>: Grant only the minimum permissions needed.
                  <strong> Avoid Wildcards</strong>: Replace <code>*</code> with explicit actions & resources.
                  <strong> Enforce MFA</strong>: Ensure MFA for console access and role assumptions.
                  <strong> Rotate Keys Regularly</strong>: PCI-DSS mandates scheduled credential rotation.
                  <strong> Use Resource-Level Permissions</strong>: Instead of <code>*</code>, specify exact ARNs.
                  <strong> Protect CloudTrail</strong>: Prevent unauthorized disabling or deleting of logs.
                  <strong> Enforce Secure Transport</strong>: Use <code>aws:SecureTransport</code> condition to require HTTPS.
                  <strong> Use Encryption at Rest</strong>: KMS or SSE for sensitive data per PCI-DSS.
                  <strong> Monitor Risky Actions</strong>: e.g. <code>iam:PassRole</code>, <code>kms:DisableKey</code>.
                  <strong> Discourage Root Usage</strong>: Root should be locked down or never used for daily tasks.
                  <strong> Check Policy Versions</strong>: Keep IAM policies updated to <code>2012-10-17</code>.
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BuyMeACoffee />
    </>
  );
};

/**
 * ToggleRow: Reusable row for toggles
 */
const ToggleRow = ({ label, enabled, onChange }) => {
  return (
    <label className="flex items-center cursor-pointer space-x-2">
      <span className="text-sm font-medium">{label}</span>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={enabled}
        onChange={onChange}
      />
      <ToggleSwitch isChecked={enabled} />
    </label>
  );
};

// A simple helper component for the toggle switches, so we don't repeat code
const ToggleSwitch = ({ isChecked }) => {
  return (
    <div
      className={`
        w-11 h-6 rounded-full peer
        transition-colors bg-gray-300 dark:bg-gray-600
        peer-checked:bg-[#FF9900]
        relative
        after:content-['']
        after:absolute after:top-[2px] after:left-[2px]
        after:bg-white after:border-gray-300 after:border
        after:rounded-full after:h-5 after:w-5
        after:transition-all peer-checked:after:translate-x-full
        peer-checked:after:border-white
      `}
    >
      {/* The switch circle is handled purely by CSS (via the `after:` pseudo-element). */}
    </div>
  );
};

export default PCIDSSAnalyser;